import { isFilterActive, type ColumnFilterValue, type FilterState } from './where';

/**
 * Compact codec for workspace state that belongs in the URL.
 *
 * A shareable filtered view is the point of a forensic tool, so filters and the
 * selected row live in the query string rather than component state. The naive
 * encoding — `JSON.stringify` then `encodeURIComponent` — costs roughly 60
 * characters per filter once braces and quotes are percent-escaped, which pushes
 * a handful of filters past what browsers and proxies handle comfortably.
 *
 * This encoding is positional and delimiter-based instead:
 *
 * ```
 * field~kind~arg1~arg2 ! field~kind~arg1 ! …
 * ```
 *
 * `!` separates filters and `~` separates a filter's parts, both chosen because
 * they survive a query string unescaped. Values are escaped only for those two
 * delimiters and the escape character itself, so ordinary search terms pass
 * through as typed and stay readable in the address bar.
 *
 * Decoding is total: any malformed segment is dropped rather than thrown, since
 * a hand-edited or truncated URL should degrade to fewer filters instead of a
 * broken page.
 */

const FILTER_SEP = '!';
const PART_SEP = '~';
const ESCAPE = '\\';

/** Cap the encoded filter string so a pathological state can't produce an unusable URL. */
export const MAX_ENCODED_FILTERS_LENGTH = 1800;

function escapePart(value: string): string {
  let out = '';
  for (const char of value) {
    if (char === ESCAPE || char === FILTER_SEP || char === PART_SEP) out += ESCAPE;
    out += char;
  }
  return out;
}

/**
 * Tokenize the encoded string into filters, each a list of parts.
 *
 * Both separators are handled in one pass rather than by splitting on `!` and
 * then on `~`: nested splits would strip the escapes on the first pass, so a
 * value containing an escaped `~` would be split apart on the second.
 */
function tokenize(raw: string): string[][] {
  const filters: string[][] = [];
  let parts: string[] = [];
  let current = '';
  let escaped = false;

  for (const char of raw) {
    if (escaped) {
      current += char;
      escaped = false;
      continue;
    }
    if (char === ESCAPE) {
      escaped = true;
      continue;
    }
    if (char === PART_SEP) {
      parts.push(current);
      current = '';
      continue;
    }
    if (char === FILTER_SEP) {
      parts.push(current);
      filters.push(parts);
      parts = [];
      current = '';
      continue;
    }
    current += char;
  }

  parts.push(current);
  filters.push(parts);
  return filters;
}

function encodeFilter(field: string, value: ColumnFilterValue): string | null {
  switch (value.kind) {
    case 'string':
      return [field, 's', value.op, value.value].map(escapePart).join(PART_SEP);
    case 'number':
      return [field, 'n', value.op, value.value].map(escapePart).join(PART_SEP);
    case 'boolean':
      return [field, 'b', value.value ? '1' : '0'].map(escapePart).join(PART_SEP);
    case 'enum':
      // Enum members are joined by a third delimiter; they cannot contain it in
      // practice (they are schema-authored values), but escape anyway.
      return [field, 'e', ...value.values].map(escapePart).join(PART_SEP);
    case 'datetime':
      return [field, 'd', value.gte ?? '', value.lte ?? ''].map(escapePart).join(PART_SEP);
    default:
      return null;
  }
}

const STRING_OPS = new Set(['contains', 'equals', 'startsWith', 'endsWith']);
const NUMBER_OPS = new Set(['equals', 'gt', 'gte', 'lt', 'lte']);

function decodeFilter(parts: readonly string[]): [string, ColumnFilterValue] | null {
  const [field, kind, ...rest] = parts;
  if (!field || !kind) return null;

  switch (kind) {
    case 's': {
      const [op, value = ''] = rest;
      if (!op || !STRING_OPS.has(op)) return null;
      return [field, { kind: 'string', op: op as never, value }];
    }
    case 'n': {
      const [op, value = ''] = rest;
      if (!op || !NUMBER_OPS.has(op)) return null;
      return [field, { kind: 'number', op: op as never, value }];
    }
    case 'b':
      return [field, { kind: 'boolean', value: rest[0] === '1' }];
    case 'e': {
      const values = rest.filter((v) => v !== '');
      if (values.length === 0) return null;
      return [field, { kind: 'enum', values }];
    }
    case 'd': {
      const [gte = '', lte = ''] = rest;
      if (!gte && !lte) return null;
      return [field, { kind: 'datetime', ...(gte ? { gte } : {}), ...(lte ? { lte } : {}) }];
    }
    default:
      return null;
  }
}

/**
 * Encode filter state for the URL. Returns an empty string when nothing is
 * active, and truncates to whole filters if the result would exceed
 * {@link MAX_ENCODED_FILTERS_LENGTH}.
 */
export function encodeFilters(filters: FilterState): string {
  const segments: string[] = [];
  let length = 0;
  for (const [field, value] of Object.entries(filters)) {
    // A filter with no value constrains nothing; keeping it would put a visible
    // chip and a URL segment behind a query that didn't change.
    if (!isFilterActive(value)) continue;
    const segment = encodeFilter(field, value);
    if (segment == null) continue;
    const added = segments.length === 0 ? segment.length : segment.length + 1;
    if (length + added > MAX_ENCODED_FILTERS_LENGTH) break;
    segments.push(segment);
    length += added;
  }
  return segments.join(FILTER_SEP);
}

/** Decode filter state from the URL, dropping any segment that doesn't parse. */
export function decodeFilters(raw: string | null | undefined): FilterState {
  if (!raw) return {};
  const filters: FilterState = {};
  for (const parts of tokenize(raw)) {
    const decoded = decodeFilter(parts);
    if (decoded) filters[decoded[0]] = decoded[1];
  }
  return filters;
}

/**
 * Filters from a `/mobile-viewer` `f` param.
 * The compact codec wins. A single `field:value` (no `~` or `!`) is a contains filter.
 */
export function filtersFromBrowseParam(raw: string | null | undefined): FilterState {
  const decoded = decodeFilters(raw);
  if (Object.keys(decoded).length > 0 || !raw || /[~!]/.test(raw)) return decoded;
  const splitAt = raw.indexOf(':');
  if (splitAt <= 0) return {};
  const field = raw.slice(0, splitAt).trim();
  const value = raw.slice(splitAt + 1).trim();
  if (!field || !value) return {};
  return { [field]: { kind: 'string', op: 'contains', value } };
}
